import { etagFetch } from '../functions/etagFetch';
import { usePromise } from './usePromise';

export type VersionStatus = 'up-to-date' | 'behind-patch' | 'behind-minor' | 'behind-major';

export interface UseVersionStatusOptions {
  currentVersion: string;
  latestUrl: string;
  etagStorageKey: string;
  valueStorageKey: string;
}

const parseVersion = (value: string): [number, number, number] => {
  const [major, minor, patch] = value
    .replace(/^v/, '')
    .split('.')
    .map(part => Number.parseInt(part, 10) || 0);

  return [major ?? 0, minor ?? 0, patch ?? 0];
};

const resolveStatus = (
  current: string,
  latest: string
): { status: VersionStatus; versionsBehind: number } => {
  const [currentMajor, currentMinor, currentPatch] = parseVersion(current);
  const [latestMajor, latestMinor, latestPatch] = parseVersion(latest);

  if (latestMajor > currentMajor) {
    return { status: 'behind-major', versionsBehind: latestMajor - currentMajor };
  }

  if (latestMajor === currentMajor && latestMinor > currentMinor) {
    return { status: 'behind-minor', versionsBehind: latestMinor - currentMinor };
  }

  if (latestMajor === currentMajor && latestMinor === currentMinor && latestPatch > currentPatch) {
    return { status: 'behind-patch', versionsBehind: latestPatch - currentPatch };
  }

  return { status: 'up-to-date', versionsBehind: 0 };
};

export function useVersionStatus({
  currentVersion,
  latestUrl,
  etagStorageKey,
  valueStorageKey
}: UseVersionStatusOptions) {
  const [latestVersion, , loading] = usePromise(
    () =>
      etagFetch<{ version?: string }>({
        url: latestUrl,
        etagStorageKey,
        valueStorageKey
      }).then(data => data?.version),
    [latestUrl, etagStorageKey, valueStorageKey]
  );

  const resolved = loading || !latestVersion ? null : resolveStatus(currentVersion, latestVersion);

  return {
    status: resolved?.status ?? null,
    versionsBehind: resolved?.versionsBehind ?? 0,
    latestVersion,
    loading,
    currentVersion
  } as const;
}
